import { Array, Data, Option, Result, Url } from '@blade/std-lib'

import type { Appearance } from '@blade/ui-theme'
import type { Lang } from '@root/shared/localization'

const KnownSocialPlatformResourceTag = {
  Youtube: 'youtube',
  Tiktok: 'tiktok',
  Instagram: 'instagram',
  Facebook: 'facebook',
  Twitch: 'twitch',
  Reddit: 'reddit',
  Telegram: 'telegram',
  X: 'x',
  Wiki: 'wiki',
  Github: 'github',
  Bluesky: 'bluesky',
  Pinterest: 'pinterest',
  Substack: 'substack',
} as const

export type EmbedType =
  | 'iframe'
  | 'x'
  | 'wiki'
  | 'github'
  | 'image'
  | 'bluesky'
  | 'facebook'
  | 'pinterest'
  | 'tiktok'

export type YoutubeMediaType =
  | 'video'
  | 'shorts'
  | 'live'
  | 'channel'
  | 'playlist'
export const matchYoutubeMediaType = Data.matchString<YoutubeMediaType>()
export class YoutubeKnownSocialPlatformResource extends Data.TaggedClass(
  KnownSocialPlatformResourceTag.Youtube,
)<{
  resourceId: string
  type: YoutubeMediaType
}> {
  embedType: EmbedType = 'iframe'

  static video = ({ resourceId }: { resourceId: string }) =>
    new YoutubeKnownSocialPlatformResource({ resourceId, type: 'video' })

  static shorts = ({ resourceId }: { resourceId: string }) =>
    new YoutubeKnownSocialPlatformResource({ resourceId, type: 'shorts' })

  static live = ({ resourceId }: { resourceId: string }) =>
    new YoutubeKnownSocialPlatformResource({ resourceId, type: 'live' })

  static channel = ({ resourceId }: { resourceId: string }) =>
    new YoutubeKnownSocialPlatformResource({ resourceId, type: 'channel' })

  static playlist = ({ resourceId }: { resourceId: string }) =>
    new YoutubeKnownSocialPlatformResource({ resourceId, type: 'playlist' })

  static fromString = (
    url: string,
  ): YoutubeKnownSocialPlatformResource | undefined => {
    const urlParseResult = Url.fromString(url)

    if (Result.isFailure(urlParseResult)) {
      return undefined
    }

    const parsedUrl = urlParseResult.success
    const hostname = parsedUrl.hostname.toLowerCase()

    const pathParts = parsedUrl.pathname.split('/').filter(Boolean)

    // youtu.be/VIDEO_ID
    if (hostname === 'youtu.be') {
      const videoId = pathParts[0]

      return videoId
        ? YoutubeKnownSocialPlatformResource.video({ resourceId: videoId })
        : undefined
    }

    // watch?v=
    const v = parsedUrl.searchParams.get('v')

    if (v) {
      return YoutubeKnownSocialPlatformResource.video({ resourceId: v })
    }

    const [resourceType, resourceId] = pathParts

    // https://www.youtube.com/playlist?list=PLqNlPVpQiRLFtMl-IGMUutj2bWYVzQUzK
    if (resourceType === 'playlist') {
      const listId = parsedUrl.searchParams.get('list')

      return listId
        ? YoutubeKnownSocialPlatformResource.playlist({ resourceId: listId })
        : undefined
    }

    if (!resourceId) {
      return undefined
    }
    //https://www.youtube.com/shorts/sFQ9jedKv8g
    if (resourceType === 'shorts') {
      return YoutubeKnownSocialPlatformResource.shorts({ resourceId })
    }

    if (resourceType === 'embed') {
      return YoutubeKnownSocialPlatformResource.video({ resourceId })
    }

    if (resourceType === 'live') {
      return YoutubeKnownSocialPlatformResource.live({ resourceId })
    }
    if (resourceType === 'channel') {
      return YoutubeKnownSocialPlatformResource.channel({ resourceId })
    }

    return undefined
  }
}

export class WikiKnownSocialPlatformResource extends Data.TaggedClass(
  KnownSocialPlatformResourceTag.Wiki,
)<{
  articleLanguage: string
  articleTitle: string
  url: string
}> {
  embedType: EmbedType = 'wiki'

  static fromString = (
    url: string,
  ): WikiKnownSocialPlatformResource | undefined => {
    const urlParseResult = Url.fromString(url)

    if (Result.isFailure(urlParseResult)) {
      return undefined
    }

    const pathSegments = urlParseResult.success.pathname
      .split('/')
      .filter(Boolean)
    const articleTitle = Array.last(pathSegments)
    const articleLanguage = Array.first(
      urlParseResult.success.hostname.split('.').filter(Boolean),
    )

    if (
      articleLanguage &&
      articleLanguage !== 'wikipedia' &&
      Option.isSome(articleTitle)
    ) {
      return new WikiKnownSocialPlatformResource({
        articleLanguage,
        articleTitle: articleTitle.value,
        url,
      })
    }

    return undefined
  }
}

export type GithubMediaType = 'profile' | 'repo'
export const matchGithubMediaType = Data.matchString<GithubMediaType>()

// Top-level GitHub paths that aren't user/organization profiles
const githubReservedPaths = new Set([
  'about',
  'account',
  'apps',
  'codespaces',
  'collections',
  'contact',
  'customer-stories',
  'dashboard',
  'developer',
  'discussions',
  'enterprise',
  'events',
  'explore',
  'features',
  'home',
  'issues',
  'login',
  'logout',
  'marketplace',
  'mobile',
  'new',
  'nonprofit',
  'notifications',
  'organizations',
  'orgs',
  'pricing',
  'pulls',
  'security',
  'settings',
  'signup',
  'sponsors',
  'stars',
  'team',
  'topics',
  'trending',
  'watching',
])

export class GithubKnownSocialPlatformResource extends Data.TaggedClass(
  KnownSocialPlatformResourceTag.Github,
)<{
  type: GithubMediaType
  username: string
  repo?: string
}> {
  get embedType(): EmbedType {
    return this.type === 'profile' ? 'github' : 'image'
  }

  get url(): string {
    return this.type === 'profile'
      ? `https://github.com/${this.username}`
      : `https://github.com/${this.username}/${this.repo}`
  }

  static profile = ({
    username,
  }: {
    username: string
  }): GithubProfileKnownSocialPlatformResource =>
    new GithubKnownSocialPlatformResource({
      type: 'profile',
      username,
    }) as GithubProfileKnownSocialPlatformResource

  static repo = ({
    username,
    repo,
  }: {
    username: string
    repo: string
  }): GithubRepoKnownSocialPlatformResource =>
    new GithubKnownSocialPlatformResource({
      type: 'repo',
      username,
      repo,
    }) as GithubRepoKnownSocialPlatformResource

  static fromString = (
    url: string,
  ): GithubKnownSocialPlatformResource | undefined => {
    const urlParseResult = Url.fromString(url)

    if (Result.isFailure(urlParseResult)) {
      return undefined
    }

    const parsedUrl = urlParseResult.success
    const hostname = parsedUrl.hostname.toLowerCase()

    if (hostname !== 'github.com' && hostname !== 'www.github.com') {
      return undefined
    }

    const pathParts = parsedUrl.pathname.split('/').filter(Boolean)

    const [username, repo] = pathParts

    if (!username || githubReservedPaths.has(username.toLowerCase())) {
      return undefined
    }

    // https://github.com/{username}
    if (pathParts.length === 1) {
      return GithubKnownSocialPlatformResource.profile({ username })
    }

    // https://github.com/{username}/{repo}
    if (pathParts.length === 2 && repo) {
      return GithubKnownSocialPlatformResource.repo({ username, repo })
    }

    return undefined
  }
}

export type GithubProfileKnownSocialPlatformResource =
  GithubKnownSocialPlatformResource & { type: 'profile' }

export type GithubRepoKnownSocialPlatformResource =
  GithubKnownSocialPlatformResource & {
    type: 'repo'
    repo: string
  }

export type TwitchMediaType = 'video' | 'clip' | 'channel'

const matchTwitchMediaType = Data.matchString<TwitchMediaType>()
export class TwitchKnownSocialPlatformResource extends Data.TaggedClass(
  KnownSocialPlatformResourceTag.Twitch,
)<{
  type: TwitchMediaType
  id: string
}> {
  embedType: EmbedType = 'iframe'

  static video = ({ id }: { id: string }) =>
    new TwitchKnownSocialPlatformResource({ type: 'video', id })

  static clip = ({ id }: { id: string }) =>
    new TwitchKnownSocialPlatformResource({ type: 'clip', id })

  static channel = ({ id }: { id: string }) =>
    new TwitchKnownSocialPlatformResource({ type: 'channel', id })

  static fromString = (
    url: string,
  ): TwitchKnownSocialPlatformResource | undefined => {
    const urlParseResult = Url.fromString(url)

    if (Result.isFailure(urlParseResult)) {
      return undefined
    }

    const parsedUrl = urlParseResult.success

    //https://www.twitch.tv/tpabomah
    //https://www.twitch.tv/videos/2775161933
    //https://www.twitch.tv/tpabomah/clip/CallousCovertShieldTebowing-Ux5Zup7sdo5r6NXt
    const [, videoOrUsername, clipOrVideoId, clipSlug] =
      parsedUrl.pathname.split('/')

    if (videoOrUsername && clipOrVideoId === undefined) {
      return TwitchKnownSocialPlatformResource.channel({ id: videoOrUsername })
    }

    if (videoOrUsername === 'videos' && clipOrVideoId) {
      return TwitchKnownSocialPlatformResource.video({ id: clipOrVideoId })
    }

    if (clipOrVideoId === 'clip' && clipSlug) {
      return TwitchKnownSocialPlatformResource.clip({ id: clipSlug })
    }

    return undefined
  }
}

export type TiktokMediaType =
  | 'video'
  | 'photo'
  | 'profile'
  | 'tag'
  | 'search'
  | 'music'
export const matchTiktokMediaType = Data.matchString<TiktokMediaType>()

export class TiktokKnownSocialPlatformResource<
  T extends 'tiktok' | 'iframe',
> extends Data.TaggedClass(KnownSocialPlatformResourceTag.Tiktok)<{
  type: TiktokMediaType
  resourceId: string
  embedType: T
}> {
  static video = ({ resourceId }: { resourceId: string }) =>
    new TiktokKnownSocialPlatformResource({
      type: 'video',
      resourceId,
      embedType: 'iframe',
    })

  static photo = ({ resourceId }: { resourceId: string }) =>
    new TiktokKnownSocialPlatformResource({
      type: 'photo',
      resourceId,
      embedType: 'iframe',
    })

  static profile = ({ resourceId }: { resourceId: string }) =>
    new TiktokKnownSocialPlatformResource({
      type: 'profile',
      resourceId,
      embedType: 'iframe',
    })

  static tag = ({ resourceId }: { resourceId: string }) =>
    new TiktokKnownSocialPlatformResource({
      type: 'tag',
      resourceId,
      embedType: 'iframe',
    })

  static search = ({ resourceId }: { resourceId: string }) =>
    new TiktokKnownSocialPlatformResource({
      type: 'search',
      resourceId,
      embedType: 'iframe',
    })

  static music = ({ resourceId }: { resourceId: string }) =>
    new TiktokKnownSocialPlatformResource({
      type: 'music',
      resourceId,
      embedType: 'iframe',
    })

  static shortLink = ({ url }: { url: string }) =>
    new TiktokKnownSocialPlatformResource({
      type: 'video',
      resourceId: url,
      embedType: 'tiktok',
    })

  get url(): string {
    return this.resourceId
  }

  static isIframeResource(
    resource: TiktokKnownSocialPlatformResource<'tiktok' | 'iframe'>,
  ): resource is TiktokKnownSocialPlatformResource<'iframe'> {
    return resource.embedType === 'iframe'
  }

  static fromString = (
    url: string,
  ): TiktokKnownSocialPlatformResource<'tiktok' | 'iframe'> | undefined => {
    const urlParseResult = Url.fromString(url)

    if (Result.isFailure(urlParseResult)) {
      return undefined
    }

    const parsedUrl = urlParseResult.success

    const hostname = parsedUrl.hostname.toLowerCase()

    const pathParts = parsedUrl.pathname.split('/').filter(Boolean)

    const shortLinkHosts = new Set(['vm.tiktok.com', 'vt.tiktok.com'])

    if (shortLinkHosts.has(hostname)) {
      const shortSlug = pathParts[0]

      if (!shortSlug) {
        return undefined
      }

      return TiktokKnownSocialPlatformResource.shortLink({ url })
    }

    const allowedHosts = ['tiktok.com', 'www.tiktok.com', 'm.tiktok.com']

    if (!allowedHosts.includes(hostname)) {
      return undefined
    }

    // https://www.tiktok.com/t/ZTBbdbyeM/
    if (pathParts[0] === 't' && pathParts[1]) {
      return TiktokKnownSocialPlatformResource.shortLink({ url })
    }

    // https://www.tiktok.com/@username/video/1234567890
    if (
      pathParts[0]?.startsWith('@') &&
      pathParts[1] === 'video' &&
      pathParts[2]
    ) {
      return TiktokKnownSocialPlatformResource.video({
        resourceId: pathParts[2],
      })
    }

    // https://www.tiktok.com/@username/photo/7663548276628606240
    if (
      pathParts[0]?.startsWith('@') &&
      pathParts[1] === 'photo' &&
      pathParts[2]
    ) {
      return TiktokKnownSocialPlatformResource.photo({
        resourceId: pathParts[2],
      })
    }

    // https://www.tiktok.com/@username
    if (pathParts.length === 1 && pathParts[0]?.startsWith('@')) {
      return TiktokKnownSocialPlatformResource.profile({
        resourceId: pathParts[0].slice(1),
      })
    }

    // https://www.tiktok.com/tag/{tagName}
    if (pathParts[0] === 'tag' && pathParts[1]) {
      return TiktokKnownSocialPlatformResource.tag({
        resourceId: pathParts[1],
      })
    }

    // https://www.tiktok.com/music/original-sound-7647103891142544136
    if (pathParts[0] === 'music' && pathParts[1]) {
      return TiktokKnownSocialPlatformResource.music({
        resourceId: pathParts[1],
      })
    }

    // https://www.tiktok.com/search?q={query}
    if (pathParts[0] === 'search') {
      const query = parsedUrl.searchParams.get('q')
      if (query) {
        return TiktokKnownSocialPlatformResource.search({ resourceId: query })
      }
    }

    return undefined
  }
}

export type InstagramMediaType = 'p' | 'reel' | 'tv'
export const matchInstagramMediaType = Data.matchString<InstagramMediaType>()

const instagramAllowedHosts = new Set([
  'instagram.com',
  'www.instagram.com',
  'm.instagram.com',
])

export class InstagramKnownSocialPlatformResource extends Data.TaggedClass(
  KnownSocialPlatformResourceTag.Instagram,
)<{
  type: InstagramMediaType
  shortcode: string
}> {
  embedType: EmbedType = 'iframe'

  static p = ({ shortcode }: { shortcode: string }) =>
    new InstagramKnownSocialPlatformResource({ type: 'p', shortcode })

  static reel = ({ shortcode }: { shortcode: string }) =>
    new InstagramKnownSocialPlatformResource({ type: 'reel', shortcode })

  static tv = ({ shortcode }: { shortcode: string }) =>
    new InstagramKnownSocialPlatformResource({ type: 'tv', shortcode })

  static fromString = (
    url: string,
  ): InstagramKnownSocialPlatformResource | undefined => {
    const urlParseResult = Url.fromString(url)

    if (Result.isFailure(urlParseResult)) {
      return undefined
    }

    const parsedUrl = urlParseResult.success

    const hostname = parsedUrl.hostname.toLowerCase()

    if (!instagramAllowedHosts.has(hostname)) {
      return undefined
    }

    const match = parsedUrl.pathname.split('/')

    const [, authorOrReel, typeOrReelCode, shortcode] = match

    //https://www.instagram.com/reel/DYm0twYRWmS/
    if (
      (authorOrReel === 'reel' ||
        authorOrReel === 'reels' ||
        authorOrReel === 'p' ||
        authorOrReel === 'tv') &&
      typeOrReelCode
    ) {
      const type = authorOrReel === 'reels' ? 'reel' : authorOrReel

      return matchInstagramMediaType(type, {
        onP: () =>
          InstagramKnownSocialPlatformResource.p({ shortcode: typeOrReelCode }),
        onReel: () =>
          InstagramKnownSocialPlatformResource.reel({
            shortcode: typeOrReelCode,
          }),
        onTv: () =>
          InstagramKnownSocialPlatformResource.tv({
            shortcode: typeOrReelCode,
          }),
      })
    }

    if (
      (typeOrReelCode === 'reel' ||
        typeOrReelCode === 'reels' ||
        typeOrReelCode === 'p' ||
        typeOrReelCode === 'tv') &&
      shortcode
    ) {
      const type = typeOrReelCode === 'reels' ? 'reel' : typeOrReelCode

      return matchInstagramMediaType(type, {
        onP: () => InstagramKnownSocialPlatformResource.p({ shortcode }),
        onReel: () => InstagramKnownSocialPlatformResource.reel({ shortcode }),
        onTv: () => InstagramKnownSocialPlatformResource.tv({ shortcode }),
      })
    }

    return undefined
  }
}

export type FacebookMediaType = 'reel' | 'video' | 'post' | 'page'
export const matchFacebookMediaType = Data.matchString<FacebookMediaType>()

const facebookAllowedHosts = new Set([
  'facebook.com',
  'www.facebook.com',
  'm.facebook.com',
  'web.facebook.com',
])

export type FacebookXfbmlClass = 'fb-post' | 'fb-video' | 'fb-page'

export class FacebookKnownSocialPlatformResource<
  T extends 'facebook' | 'iframe',
> extends Data.TaggedClass(KnownSocialPlatformResourceTag.Facebook)<{
  type: FacebookMediaType
  id: string
  username: string
  embedType: T
}> {
  get url(): string {
    return toFacebookEmbedHref(this)
  }

  get xfbmlClass(): FacebookXfbmlClass {
    return toFacebookXfbmlClass(this)
  }

  static reel = (args: { id: string }) =>
    new FacebookKnownSocialPlatformResource({
      type: 'reel',
      id: args.id,
      username: '',
      embedType: 'iframe',
    })

  static video = (args: { username: string; id: string }) =>
    new FacebookKnownSocialPlatformResource({
      type: 'video',
      id: args.id,
      username: args.username,
      embedType: 'iframe',
    })

  static post = (args: { username: string; id: string }) =>
    new FacebookKnownSocialPlatformResource({
      type: 'post',
      id: args.id,
      username: args.username,
      embedType: 'facebook',
    })

  static page = (args: { username: string }) =>
    new FacebookKnownSocialPlatformResource({
      type: 'page',
      id: '',
      username: args.username,
      embedType: 'iframe',
    })

  static isIframeResource(
    resource: FacebookKnownSocialPlatformResource<'facebook' | 'iframe'>,
  ): resource is FacebookKnownSocialPlatformResource<'iframe'> {
    return resource.embedType === 'iframe'
  }

  static fromString = (
    url: string,
  ): FacebookKnownSocialPlatformResource<'iframe' | 'facebook'> | undefined => {
    const urlParseResult = Url.fromString(url)

    if (Result.isFailure(urlParseResult)) {
      return undefined
    }

    const parsedUrl = urlParseResult.success
    const hostname = parsedUrl.hostname.toLowerCase()

    if (!facebookAllowedHosts.has(hostname)) {
      return undefined
    }

    const pathParts = parsedUrl.pathname.split('/').filter(Boolean)

    // https://www.facebook.com/facebook
    if (pathParts.length === 1 && pathParts[0]) {
      return FacebookKnownSocialPlatformResource.page({
        username: pathParts[0],
      })
    }

    // https://www.facebook.com/reel/3524687381002928/
    // https://www.facebook.com/reel/3524687381002928
    if (pathParts[0] === 'reel' && pathParts[1]) {
      return FacebookKnownSocialPlatformResource.reel({ id: pathParts[1] })
    }

    // https://www.facebook.com/groups/{groupId}/posts/{postId}/
    if (
      pathParts[0] === 'groups' &&
      pathParts[1] &&
      pathParts[2] === 'posts' &&
      pathParts[3]
    ) {
      return FacebookKnownSocialPlatformResource.post({
        username: pathParts[1],
        id: pathParts[3],
      })
    }

    // https://www.facebook.com/{username}/videos/{video_id}/
    if (
      pathParts.length >= 3 &&
      pathParts[1] === 'videos' &&
      pathParts[0] &&
      pathParts[2]
    ) {
      return FacebookKnownSocialPlatformResource.video({
        username: pathParts[0],
        id: pathParts[2],
      })
    }
    // https://www.facebook.com/facebook/posts/pfbid02Z9Z1T6W8qkMVWUMqM9C4MLtvyG4xdTS6SC3pkXWKtwp99JJ3hTxzDr5RmorHj8axl
    if (
      pathParts.length >= 3 &&
      pathParts[1] === 'posts' &&
      pathParts[0] &&
      pathParts[2]
    ) {
      return FacebookKnownSocialPlatformResource.post({
        username: pathParts[0],
        id: pathParts[2],
      })
    }

    return undefined
  }
}

export class XKnownSocialPlatformResource extends Data.TaggedClass(
  KnownSocialPlatformResourceTag.X,
)<{
  tweetId: string
}> {
  readonly embedType: EmbedType = 'x'

  static fromString = (
    url: string,
  ): XKnownSocialPlatformResource | undefined => {
    const urlParseResult = Url.fromString(url)

    if (Result.isFailure(urlParseResult)) {
      return undefined
    }

    const parsedUrl = urlParseResult.success

    const [, _user, status, tweetId] = parsedUrl.pathname.split('/')

    if (status === 'status' && tweetId) {
      return new XKnownSocialPlatformResource({
        tweetId,
      })
    }

    return undefined
  }
}

// https://www.reddit.com/r/{subreddit}/comments/{postId}/{slug}/
// embeds to https://www.redditmedia.com/r/{subreddit}/comments/{postId}/{slug}/?embed=true&theme=dark
export class RedditKnownSocialPlatformResource extends Data.TaggedClass(
  KnownSocialPlatformResourceTag.Reddit,
)<{
  subreddit: string
  postId: string
  slug: string
}> {
  embedType: EmbedType = 'iframe'

  static fromString = (
    url: string,
  ): RedditKnownSocialPlatformResource | undefined => {
    const urlParseResult = Url.fromString(url)

    if (Result.isFailure(urlParseResult)) {
      return undefined
    }

    const parsedUrl = urlParseResult.success
    const hostname = parsedUrl.hostname.toLowerCase()

    // redd.it short links (https://redd.it/{postId}) can't be embedded
    // without resolving the subreddit/slug, which requires a network call.
    if (hostname === 'redd.it' || hostname.endsWith('.redd.it')) {
      return undefined
    }

    const pathParts = parsedUrl.pathname.split('/').filter(Boolean)

    // https://www.reddit.com/r/{subreddit}/comments/{postId}/{slug?}/
    if (
      pathParts[0] === 'r' &&
      pathParts[1] &&
      pathParts[2] === 'comments' &&
      pathParts[3]
    ) {
      return new RedditKnownSocialPlatformResource({
        subreddit: pathParts[1],
        postId: pathParts[3],
        slug: pathParts[4] ?? '',
      })
    }

    // https://www.reddit.com/user/{username}/comments/{postId}/{slug?}/
    // Reddit Media exposes user posts under the synthetic "u_{username}" subreddit.
    if (
      (pathParts[0] === 'user' || pathParts[0] === 'u') &&
      pathParts[1] &&
      pathParts[2] === 'comments' &&
      pathParts[3]
    ) {
      return new RedditKnownSocialPlatformResource({
        subreddit: `u_${pathParts[1]}`,
        postId: pathParts[3],
        slug: pathParts[4] ?? '',
      })
    }

    return undefined
  }
}

// https://t.me/{channel}/{postId}
const telegramReservedPaths = new Set([
  'addemoji',
  'addstickers',
  'addtheme',
  'auth',
  'bg',
  'boost',
  'bot',
  'confirmphone',
  'contact',
  'giftcode',
  'invoice',
  'iv',
  'joinchat',
  'login',
  'm',
  'premium',
  'proxy',
  'setlanguage',
  'share',
  'socks',
])

export class TelegramKnownSocialPlatformResource extends Data.TaggedClass(
  KnownSocialPlatformResourceTag.Telegram,
)<{
  channel: string
  postId: string
}> {
  embedType: EmbedType = 'iframe'

  static post = (args: { channel: string; postId: string }) =>
    new TelegramKnownSocialPlatformResource({
      channel: args.channel,
      postId: args.postId,
    })

  static fromString = (
    url: string,
  ): TelegramKnownSocialPlatformResource | undefined => {
    const urlParseResult = Url.fromString(url)

    if (Result.isFailure(urlParseResult)) {
      return undefined
    }

    const parsedUrl = urlParseResult.success
    const hostname = parsedUrl.hostname.toLowerCase().replace(/^www\./, '')

    const allowedHosts = [
      'telegram.me',
      'telegram.dog',
      't.me',
      'telegram.org',
    ] as const

    if (!allowedHosts.includes(hostname as (typeof allowedHosts)[number])) {
      return undefined
    }

    const pathParts = parsedUrl.pathname.split('/').filter(Boolean)

    // https://t.me/s/{channel}/{postId}
    if (pathParts[0] === 's' && pathParts[1] && pathParts[2]) {
      return TelegramKnownSocialPlatformResource.post({
        channel: pathParts[1],
        postId: pathParts[2],
      })
    }

    const [channel, postId] = pathParts

    // https://t.me/{channel}/{postId}
    if (
      channel &&
      postId &&
      pathParts.length === 2 &&
      !telegramReservedPaths.has(channel.toLowerCase())
    ) {
      return TelegramKnownSocialPlatformResource.post({
        channel,
        postId,
      })
    }

    return undefined
  }
}

// https://bsky.app/profile/{handle-or-did}/post/{rkey}
// embeds via oEmbed: https://embed.bsky.app/oembed?url={post-url}
export class BlueskyKnownSocialPlatformResource extends Data.TaggedClass(
  KnownSocialPlatformResourceTag.Bluesky,
)<{
  profile: string
  postId: string
}> {
  embedType: EmbedType = 'bluesky'

  get url(): string {
    return `https://bsky.app/profile/${this.profile}/post/${this.postId}`
  }

  static fromString = (
    url: string,
  ): BlueskyKnownSocialPlatformResource | undefined => {
    const urlParseResult = Url.fromString(url)

    if (Result.isFailure(urlParseResult)) {
      return undefined
    }

    const parsedUrl = urlParseResult.success
    const hostname = parsedUrl.hostname.toLowerCase()

    if (hostname !== 'bsky.app' && hostname !== 'www.bsky.app') {
      return undefined
    }

    const pathParts = parsedUrl.pathname.split('/').filter(Boolean)

    // https://bsky.app/profile/{handle-or-did}/post/{rkey}
    if (
      pathParts[0] === 'profile' &&
      pathParts[1] &&
      pathParts[2] === 'post' &&
      pathParts[3]
    ) {
      return new BlueskyKnownSocialPlatformResource({
        profile: pathParts[1],
        postId: pathParts[3],
      })
    }

    return undefined
  }
}

export type PinterestMediaType = 'pin' | 'user' | 'board'
export const matchPinterestMediaType = Data.matchString<PinterestMediaType>()

const PINTEREST_NON_PROFILE_PATH_SEGMENTS = new Set([
  'about',
  'business',
  'creators',
  'help',
  'ideas',
  'login',
  'password',
  'pin',
  'policy',
  'privacy',
  'search',
  'settings',
  'shopping',
  'signup',
  'terms',
  'today',
  'videos',
])

// https://www.pinterest.com/pin/{pinId}/
// https://www.pinterest.com/{username}/
// https://www.pinterest.com/{username}/{boardSlug}/
export class PinterestKnownSocialPlatformResource extends Data.TaggedClass(
  KnownSocialPlatformResourceTag.Pinterest,
)<{
  type: PinterestMediaType
  id: string
  boardSlug?: string
}> {
  readonly embedType: EmbedType = 'pinterest'

  get embedUrl(): string {
    return matchPinterestMediaType(this.type, {
      onPin: () => toPinterestPinUrl(this.id),
      onUser: () => toPinterestUserUrl(this.id),
      onBoard: () => toPinterestBoardUrl(this.id, this.boardSlug ?? ''),
    })
  }

  static pin = ({ id }: { id: string }) =>
    new PinterestKnownSocialPlatformResource({
      type: 'pin',
      id,
    })

  static user = ({ id }: { id: string }) =>
    new PinterestKnownSocialPlatformResource({
      type: 'user',
      id,
    })

  static board = ({ id, boardSlug }: { id: string; boardSlug: string }) =>
    new PinterestKnownSocialPlatformResource({
      type: 'board',
      id,
      boardSlug,
    })

  static fromString = (
    url: string,
  ): PinterestKnownSocialPlatformResource | undefined => {
    const urlParseResult = Url.fromString(url)

    if (Result.isFailure(urlParseResult)) {
      return undefined
    }

    const pathParts = urlParseResult.success.pathname.split('/').filter(Boolean)

    if (pathParts[0] === 'pin' && pathParts[1]) {
      return PinterestKnownSocialPlatformResource.pin({ id: pathParts[1] })
    }

    const [username, boardSlug] = pathParts

    if (
      username &&
      boardSlug &&
      pathParts.length === 2 &&
      !PINTEREST_NON_PROFILE_PATH_SEGMENTS.has(username) &&
      !PINTEREST_NON_PROFILE_PATH_SEGMENTS.has(boardSlug)
    ) {
      return PinterestKnownSocialPlatformResource.board({
        id: username,
        boardSlug,
      })
    }

    if (
      username &&
      pathParts.length === 1 &&
      !PINTEREST_NON_PROFILE_PATH_SEGMENTS.has(username)
    ) {
      return PinterestKnownSocialPlatformResource.user({ id: username })
    }

    return undefined
  }
}

// https://{publication}.substack.com/p/{slug}
// embeds via iframe: https://{publication}.substack.com/embed/p/{slug}
export class SubstackKnownSocialPlatformResource extends Data.TaggedClass(
  KnownSocialPlatformResourceTag.Substack,
)<{
  publicationUrl: string
  slug: string
}> {
  readonly embedType: EmbedType = 'iframe'

  get url(): string {
    return `${this.publicationUrl}/p/${this.slug}`
  }

  static fromString = (
    url: string,
  ): SubstackKnownSocialPlatformResource | undefined => {
    const urlParseResult = Url.fromString(url)

    if (Result.isFailure(urlParseResult)) {
      return undefined
    }

    const parsedUrl = urlParseResult.success

    const pathParts = parsedUrl.pathname.split('/').filter(Boolean)

    // https://{publication}.substack.com/p/{slug}
    if (pathParts[0] === 'p' && pathParts[1]) {
      return new SubstackKnownSocialPlatformResource({
        publicationUrl: parsedUrl.origin,
        slug: pathParts[1],
      })
    }

    return undefined
  }
}

export const toPinterestPinUrl = (pinId: string): string =>
  `https://www.pinterest.com/pin/${pinId}/`

export const toPinterestUserUrl = (username: string): string =>
  `https://www.pinterest.com/${username}/`

export const toPinterestBoardUrl = (
  username: string,
  boardSlug: string,
): string => `https://www.pinterest.com/${username}/${boardSlug}/`

export type IframeEmbedableKnownSocialPlatformResource =
  | YoutubeKnownSocialPlatformResource
  | TiktokKnownSocialPlatformResource<'iframe'>
  | InstagramKnownSocialPlatformResource
  | FacebookKnownSocialPlatformResource<'iframe'>
  | TwitchKnownSocialPlatformResource
  | RedditKnownSocialPlatformResource
  | TelegramKnownSocialPlatformResource
  | SubstackKnownSocialPlatformResource

export const matchIframeEmbedable =
  Data.matchFor<IframeEmbedableKnownSocialPlatformResource>()

export type ImageEmbedableKnownSocialPlatformResource =
  GithubRepoKnownSocialPlatformResource

export type KnownSocialPlatformResource =
  | IframeEmbedableKnownSocialPlatformResource
  | XKnownSocialPlatformResource
  | WikiKnownSocialPlatformResource
  | GithubKnownSocialPlatformResource
  | BlueskyKnownSocialPlatformResource
  | FacebookKnownSocialPlatformResource<'facebook'>
  | PinterestKnownSocialPlatformResource
  | TiktokKnownSocialPlatformResource<'tiktok'>

export const match = Data.matchFor<KnownSocialPlatformResource>()

export const matchEmbedType = <I, X, W, G, IMG, B, F, P, T>(
  resource: KnownSocialPlatformResource,
  matchers: {
    onIframe: (resource: IframeEmbedableKnownSocialPlatformResource) => I
    onX: (resource: XKnownSocialPlatformResource) => X
    onWiki: (resource: WikiKnownSocialPlatformResource) => W
    onGithub: (resource: GithubProfileKnownSocialPlatformResource) => G
    onImage: (resource: ImageEmbedableKnownSocialPlatformResource) => IMG
    onBluesky: (resource: BlueskyKnownSocialPlatformResource) => B
    onFacebook: (resource: FacebookKnownSocialPlatformResource<'facebook'>) => F
    onPinterest: (resource: PinterestKnownSocialPlatformResource) => P
    onTiktok: (resource: TiktokKnownSocialPlatformResource<'tiktok'>) => T
  },
): I | X | W | G | IMG | B | F | P | T =>
  match(resource, {
    youtube: (resource) => matchers.onIframe(resource),
    tiktok: (resource) => {
      return resource.embedType === 'iframe'
        ? matchers.onIframe(resource)
        : matchers.onTiktok(resource)
    },
    instagram: (resource) => matchers.onIframe(resource),
    facebook: (resource) => {
      return resource.embedType === 'iframe'
        ? matchers.onIframe(resource)
        : matchers.onFacebook(resource)
    },
    twitch: (resource) => matchers.onIframe(resource),
    reddit: (resource) => matchers.onIframe(resource),
    telegram: (resource) => matchers.onIframe(resource),
    substack: (resource) => matchers.onIframe(resource),
    bluesky: (resource) => matchers.onBluesky(resource),
    pinterest: (resource) => matchers.onPinterest(resource),
    x: (resource) => matchers.onX(resource),
    wiki: (resource) => matchers.onWiki(resource),
    github: (resource) =>
      resource.type === 'profile'
        ? matchers.onGithub(
            resource as GithubProfileKnownSocialPlatformResource,
          )
        : matchers.onImage(resource as GithubRepoKnownSocialPlatformResource),
  })

export const hasToShowEmbedInTauri = (resource: KnownSocialPlatformResource) =>
  match(resource, {
    youtube: () => true,
    twitch: () => true,
    tiktok: () => true,
    instagram: () => true,
    facebook: () => true,
    reddit: () => false,
    telegram: () => true,
    substack: () => false,
    bluesky: () => true,
    pinterest: () => true,
    x: () => true,
    wiki: () => true,
    github: () => true,
  })

export const toRepoOpenGraphImageUrl = (
  resource: GithubRepoKnownSocialPlatformResource,
): URL =>
  new URL(
    `https://opengraph.githubassets.com/1/${resource.username}/${resource.repo}`,
  )

export const toIframeEmbedUrl = (
  resource: IframeEmbedableKnownSocialPlatformResource,
  options: {
    lang?: Lang.Lang
    appearance: Appearance
    twitch: {
      parent: string
    }
    youtube: {
      origin: string
    }
  },
): URL => {
  const appearance = options.appearance

  return matchIframeEmbedable(resource, {
    youtube: (resource) =>
      matchYoutubeMediaType(resource.type, {
        onShorts: () =>
          new URL(
            `https://www.youtube-nocookie.com/embed/${resource.resourceId}?origin=${options.youtube.origin}`,
          ),
        onLive: () =>
          new URL(
            `https://www.youtube-nocookie.com/embed/${resource.resourceId}?origin=${options.youtube.origin}`,
          ),
        onVideo: () =>
          new URL(
            `https://www.youtube-nocookie.com/embed/${resource.resourceId}?origin=${options.youtube.origin}`,
          ),
        onChannel: () => {
          const url = new URL('https://www.youtube.com/embed/videoseries')

          const listId = youtubeChannelIdToListId(resource.resourceId)

          url.searchParams.set('list', listId)
          return url
        },
        onPlaylist: () => {
          const url = new URL('https://www.youtube.com/embed/videoseries')

          url.searchParams.set('list', resource.resourceId)
          return url
        },
      }),
    tiktok: (resource) => toTiktokEmbedUrl(resource, options),
    instagram: (resource) =>
      new URL(
        `https://www.instagram.com/${resource.type}/${resource.shortcode}/embed`,
      ),
    facebook: (resource) => toFacebookIframeEmbedUrl(resource),
    twitch: (resource) =>
      matchTwitchMediaType(resource.type, {
        onClip: () =>
          new URL(
            `https://clips.twitch.tv/embed?clip=${resource.id}&parent=${options.twitch.parent}`,
          ),
        onVideo: () =>
          new URL(
            //the video ID must have a “v” prefix.
            `https://player.twitch.tv/?video=v${resource.id}&parent=${options.twitch.parent}`,
          ),
        onChannel: () =>
          new URL(
            `https://player.twitch.tv/?channel=${resource.id}&parent=${options.twitch.parent}`,
          ),
      }),
    reddit: (resource) => toRedditMediaEmbedUrl(resource, appearance),
    telegram: (resource) => toTelegramEmbedUrl(resource, appearance),
    substack: (resource) =>
      toSubstackEmbedUrl(resource, { origin: options.youtube.origin }),
  })
}

const TAURI_EMBED_BASE = 'http://localhost:12582/embed'

export const toTauriUrl = (
  resource: IframeEmbedableKnownSocialPlatformResource,
  options: {
    lang?: Lang.Lang
    appearance: Appearance
  },
): URL => {
  const appearance = options.appearance

  return matchIframeEmbedable(resource, {
    youtube: (resource) => {
      const resourceId =
        resource.type === 'channel'
          ? youtubeChannelIdToListId(resource.resourceId)
          : resource.resourceId

      return new URL(
        `${TAURI_EMBED_BASE}/youtube?v=${encodeURIComponent(resourceId)}&kind=${encodeURIComponent(resource.type)}`,
      )
    },
    twitch: (resource) =>
      new URL(
        `${TAURI_EMBED_BASE}/twitch?id=${encodeURIComponent(
          resource.id,
        )}&kind=${encodeURIComponent(resource.type)}`,
      ),
    tiktok: (resource) => toTiktokEmbedUrl(resource, options),
    instagram: (resource) =>
      new URL(
        `https://www.instagram.com/${resource.type}/${resource.shortcode}/embed`,
      ),
    facebook: (resource) => toFacebookIframeEmbedUrl(resource, { tauri: true }),
    reddit: (resource) => toRedditMediaEmbedUrl(resource, appearance),
    telegram: (resource) => toTelegramEmbedUrl(resource, appearance),
    substack: (resource) => toSubstackEmbedUrl(resource),
  })
}

const toFacebookIframeEmbedUrl = (
  resource: FacebookKnownSocialPlatformResource<'iframe'>,
  options: { tauri: boolean } = { tauri: false },
): URL => {
  const url = matchFacebookMediaType(resource.type, {
    onReel: () => {
      const videoUrl = new URL('https://www.facebook.com/plugins/video.php')
      videoUrl.searchParams.set(
        'href',
        `https://www.facebook.com/reel/${resource.id}/`,
      )
      if (options.tauri) {
        videoUrl.searchParams.set('show_text', 'true')
      }
      return videoUrl
    },
    onVideo: () => {
      const videoUrl = new URL('https://www.facebook.com/plugins/video.php')
      videoUrl.searchParams.set(
        'href',
        `https://www.facebook.com/${resource.username}/videos/${resource.id}/`,
      )
      if (options.tauri) {
        videoUrl.searchParams.set('show_text', 'true')
      }
      return videoUrl
    },
    onPost: () => {
      throw new Error('Facebook post is not iframe embeddable')
    },
    onPage: () => {
      const pageUrl = new URL('https://www.facebook.com/plugins/page.php')
      pageUrl.searchParams.set(
        'href',
        `https://www.facebook.com/${resource.username}/`,
      )
      if (options.tauri) {
        pageUrl.searchParams.set('height', '500')
        pageUrl.searchParams.set('width', '340')
        pageUrl.searchParams.set('tabs', 'timeline, events, messages')
        pageUrl.searchParams.set('hide_cover', 'false')
      }
      return pageUrl
    },
  })

  if (!options.tauri) {
    url.searchParams.set('show_text', 'true')
  }

  return url
}

const toTiktokEmbedUrl = (
  resource: TiktokKnownSocialPlatformResource<'iframe'>,
  options?: {
    lang?: Lang.Lang
  },
): URL => {
  const toTiktokPlayerV1EmbedUrl = (): URL => {
    const url = new URL(
      `https://www.tiktok.com/player/v1/${resource.resourceId}`,
    )
    url.searchParams.set('autoplay', '1')
    url.searchParams.set('loop', '1')
    url.searchParams.set('fullscreen_button', '0')
    url.searchParams.set('description', '1')
    url.searchParams.set('music_info', '1')
    return url
  }

  const url = matchTiktokMediaType(resource.type, {
    onVideo: toTiktokPlayerV1EmbedUrl,
    onPhoto: toTiktokPlayerV1EmbedUrl,
    onProfile: () =>
      new URL(`https://www.tiktok.com/embed/@${resource.resourceId}`),
    onTag: () =>
      new URL(`https://www.tiktok.com/embed/tag/${resource.resourceId}`),
    onSearch: () =>
      new URL(
        `https://www.tiktok.com/embed/tag/${encodeURIComponent(resource.resourceId)}`,
      ),
    onMusic: () =>
      new URL(`https://www.tiktok.com/embed/music/${resource.resourceId}`),
  })
  if (options?.lang) {
    url.searchParams.set('lang', options.lang)
  }
  return url
}

const toTelegramEmbedUrl = (
  resource: TelegramKnownSocialPlatformResource,
  appearance: Appearance,
): URL => {
  const url = new URL(`https://t.me/${resource.channel}/${resource.postId}`)
  url.searchParams.set('embed', '1')
  if (appearance === 'dark') {
    url.searchParams.set('dark', '1')
  }
  return url
}

const toSubstackEmbedUrl = (
  resource: SubstackKnownSocialPlatformResource,
  options: { origin?: string } = {},
): URL => {
  const url = new URL(`${resource.publicationUrl}/embed/p/${resource.slug}`)
  // Substack's embed child frame uses the parent origin to target its
  // resize postMessage, mirroring the official embed.js behaviour.
  if (options.origin) {
    url.searchParams.set('origin', options.origin)
  }
  return url
}

const toRedditMediaEmbedUrl = (
  resource: RedditKnownSocialPlatformResource,
  appearance: Appearance,
): URL => {
  const slugSegment = resource.slug ? `${resource.slug}/` : ''
  const url = new URL(
    `https://www.redditmedia.com/r/${resource.subreddit}/comments/${resource.postId}/${slugSegment}`,
  )
  url.searchParams.set('embed', 'true')
  url.searchParams.set('theme', appearance)
  return url
}

export const toFacebookEmbedHref = (
  resource: FacebookKnownSocialPlatformResource<'iframe' | 'facebook'>,
): string =>
  matchFacebookMediaType(resource.type, {
    onReel: () => `https://www.facebook.com/reel/${resource.id}/`,
    onVideo: () =>
      `https://www.facebook.com/${resource.username}/videos/${resource.id}/`,
    onPost: () =>
      `https://www.facebook.com/${resource.username}/posts/${resource.id}/`,
    onPage: () => `https://www.facebook.com/${resource.username}/`,
  })

export const toFacebookXfbmlClass = (
  resource: FacebookKnownSocialPlatformResource<'iframe' | 'facebook'>,
): FacebookXfbmlClass =>
  matchFacebookMediaType(resource.type, {
    onReel: (): FacebookXfbmlClass => 'fb-video',
    onVideo: (): FacebookXfbmlClass => 'fb-video',
    onPost: (): FacebookXfbmlClass => 'fb-post',
    onPage: (): FacebookXfbmlClass => 'fb-page',
  })

export const toBlueskyOembedUrl = (
  resource: BlueskyKnownSocialPlatformResource,
): URL => {
  const url = new URL('https://embed.bsky.app/oembed')
  url.searchParams.set('url', resource.url)
  return url
}

export const toTiktokOembedUrl = (
  resource: TiktokKnownSocialPlatformResource<'tiktok'>,
): URL => {
  const url = new URL('https://www.tiktok.com/oembed')
  url.searchParams.set('url', resource.url)
  return url
}

const youtubeChannelIdToListId = (channelId: string) => {
  const channelIdArray = [...channelId]
  channelIdArray[1] = 'U'
  return channelIdArray.join('')
}
